import React from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Building2, ChevronRight, Lock } from 'lucide-react';
import { toast } from 'sonner';

function dashboardFor(m) {
  const role = m.portal_role || m.role || '';
  if (role === 'hoa_owner') return { path: '/hoa-owner/dashboard', label: 'Enter Homeowner Portal' };
  if (role === 'landlord') return { path: '/landlord/dashboard', label: 'Enter Landlord Portal' };
  if (role === 'property_manager') return { path: '/property-manager/dashboard', label: 'Enter Property Manager Portal' };
  return { path: '/hoa-admin/dashboard', label: 'Enter HOA Administration' };
}

export default function OrgSelector({ memberships, onSelected }) {
  const navigate = useNavigate();

  const selectMembership = async (m) => {
    if (m.access_status === 'locked') {
      toast.error(m.access_reason || 'This organization is currently locked.');
      return;
    }
    try {
      const { data } = await api.post('/auth/select-membership', { membership_id: m.id });
      if (data.access_status === 'locked') {
        toast.error(data.message || 'This organization is currently locked.');
        return;
      }
      if (data.access_token) localStorage.setItem('access_token', data.access_token);
      if (data.role) localStorage.setItem('user_role', data.role);
      if (data.org_id) localStorage.setItem('active_org_id', data.org_id);
      if (data.organization_name) localStorage.setItem('active_org_name', data.organization_name);
      if (data.membership_id) localStorage.setItem('active_membership_id', data.membership_id);
      if (onSelected) onSelected(data);
      const target = data.dashboard_url || dashboardFor(m).path;
      navigate(target);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to select organization');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="org-selector">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <Building2 className="h-10 w-10 mx-auto text-emerald-600" />
          <CardTitle className="mt-3 text-lg">Choose Organization / Role</CardTitle>
          <p className="text-sm text-gray-500">Your APC EstateFlow account has access to multiple organizations. Select one to continue.</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {memberships.map((m) => {
            const isLocked = m.access_status === 'locked';
            const portal = dashboardFor(m);
            return (
              <button
                key={m.id || m.org_id}
                onClick={() => selectMembership(m)}
                disabled={isLocked}
                data-testid={`org-select-${m.org_id}`}
                className={`w-full flex items-center justify-between p-4 rounded-lg border transition-colors text-left group ${
                  isLocked
                    ? 'border-gray-200 opacity-60 cursor-not-allowed bg-gray-50'
                    : 'border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-950'
                }`}
              >
                <div>
                  <p className="font-semibold text-sm">{m.organization_name || 'Unknown'}</p>
                  <p className="text-xs text-gray-500">{m.role_label || m.staff_role_name || m.staff_role || m.portal_role || m.role || 'Member'}</p>
                  {isLocked && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <Lock className="h-3 w-3" /> {m.access_reason || 'Access locked'}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!isLocked && <span className="text-xs text-emerald-600 font-medium hidden sm:inline">{portal.label}</span>}
                  <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-emerald-600 transition-colors" />
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
